import React, { useContext } from "react";
import { GameById, GameRules, useGameTopic, useTopicMessages } from "@seabrookstudios/pitch2table";
import { FullScreenDialog } from "../core/FullScreenDialog";
import Box from "@mui/material/Box";
import CardHeader from "@mui/material/CardHeader";
import { Message } from "./Message";
import Skeleton from "@mui/material/Skeleton";
import { useGameIdFromRoute } from "../games/WithGameIdFromRoute";
import html2pdf from "html2pdf.js";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import { CachedTokenContext } from "@seabrookstudios/auth";
import { useUploadPdfBlob } from "../users/queries";
import { enqueueSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { s2Url } from "@seabrookstudios/types";
import endpoints from "../endpoints";
import { Path } from "../Path";
import { useNavigate } from "react-router-dom";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").TopicId} props.topicId
 * @param {string|undefined} [props.tooltip]
 */
export const PrintTopic = ({ topicId, tooltip }) => {
  const gameId = useGameIdFromRoute();
  const navigate = useNavigate();
  const { data: topic } = useGameTopic(gameId, topicId);
  const { data: messages = [] } = useTopicMessages(topicId);

  const token = useContext(CachedTokenContext);
  const queryClient = useQueryClient();

  const onFileUploadSuccess = async () => {
    enqueueSnackbar("Latest rules uploaded", { variant: "success" });
    await queryClient.invalidateQueries({ queryKey: GameRules(gameId) });
    await queryClient.invalidateQueries({ queryKey: GameById(gameId) });

    navigate(Path.game(gameId).rules());
  };

  const onFileUploadFailure = () => {
    enqueueSnackbar("An error occurred uploading the rules", { variant: "error" });
  };

  const { games } = endpoints();
  const url = games().rules(gameId);

  const uploadPdf = useUploadPdfBlob(s2Url(url), token, "rules", onFileUploadSuccess, onFileUploadFailure);

  const doPrint = async () => {
    if (!topic) {
      return;
    }

    const element = document.getElementById("to-print");
    if (!element) {
      return;
    }

    const options = {
      margin: 1,
      jsPDF: {
        format: "a4",
        compress: true,
      },
    };

    const filename = `${topic.name}.pdf`;

    const pdf = await html2pdf().set(options).from(element).toPdf().get("pdf");
    const pdfBlob = pdf.output("blob");

    await uploadPdf(pdfBlob, filename);
  };

  return (
    <FullScreenDialog
      title="Print"
      canSave={false}
      Icon={PictureAsPdf}
      disabled={!topicId}
      tooltip={tooltip}
      actionText="EXPORT PDF TO RULES"
      onSaveHandler={doPrint}
    >
      <Box id="to-print">
        {!topic ? <Skeleton /> : <CardHeader title={topic?.name} />}
        {messages.map((message, i) => (
          <Message key={message.id} message={message} isFirst={i === 0} readOnly hideMeta />
        ))}
      </Box>
    </FullScreenDialog>
  );
};
